import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { Avatar, Drawer, Dropdown, Icon, Menu } from 'grab-ui';
import { Mobile, ExceptMobile } from 'components/v2/responsive';
import DivButton from 'components/v2/div-button/div-button';
import LanguageSelector from 'components/v2/app-bar/language-selector/language-selector';
import AppSider from 'components/v2/app-bar/app-sider/app-sider';
import { trackClick, event, cat, custom } from 'common/utils/instrumentation';
import _get from 'lodash/get';
import CompanySelectorDesktop from 'components/v2/app-bar/company-selector/company-selector-desktop';
import CompanySelectorMobile from 'components/v2/app-bar/company-selector/company-selector-mobile';
import {
  getUserName,
  getCompanyID,
  saveSelectedCompanyAndGroup,
  isUsingPaxPersonalPayments,
  setUsePaxPersonalPayments,
  getCompanyName,
  getGroupName,
} from 'helper/user';
import {
  STORAGE_LOCALE,
  STORAGE_USER_COUNTRY_CODE,
  STORAGE_USER_COUNTRY_CODE_PERSONAL,
  SUPPORTED_LOCALES,
} from 'constants/app-constants';
import {
  PAX_USER_PROFILE_ID,
  PAX_USER_PROFILE,
} from 'constants/booking-constants';
import { isPaxUser } from 'helper/grab-id-auth-helper';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_EVENT_PARAMS,
  SCRIBE_COMMON_EVENTS,
  SCRIBE_COMMON_EVENTS_STATE_PATH_MAPPING,
} from 'applications/scribe/instrumentations/track';

import css from './app-menu.less';

const { Item } = Menu;

class AppMenu extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mobileDrawerOpening: false,
    };
  }

  onChangeProfileAndGroup = (profileID, groupID) => {
    if (!profileID || !groupID) return;

    const { userInfo } = this.props;
    const companies = _get(userInfo, 'companies', []);

    if (isPaxUser() && profileID === PAX_USER_PROFILE_ID) {
      // Switching to personal profile
      setUsePaxPersonalPayments(true);

      // Always update country code when switching to personal profile
      const personalProfileCountryCode = window.localStorage.getItem(
        STORAGE_USER_COUNTRY_CODE_PERSONAL,
      );

      if (personalProfileCountryCode) {
        window.localStorage.setItem(
          STORAGE_USER_COUNTRY_CODE,
          personalProfileCountryCode,
        );
      }
    } else {
      // Switching to enterprise profile
      saveSelectedCompanyAndGroup(profileID, groupID);
      setUsePaxPersonalPayments(false);

      const selectedCompany = companies.find(
        company => String(company.id) === String(profileID),
      );

      if (selectedCompany && selectedCompany.countryCode) {
        window.localStorage.setItem(
          STORAGE_USER_COUNTRY_CODE,
          selectedCompany.countryCode,
        );
      }
    }

    window.location.reload();
  };

  onClickMenuItem = item => {
    // profile group select
    if (_get(item, 'item.props.data-type') === 'groupSelect') {
      const profileID = _get(item, 'item.props.data-profile-id');
      const groupID = _get(item, 'item.props.data-group-id');

      this.onChangeProfileAndGroup(profileID, groupID);
      return;
    }

    const itemKey = item.key || '';
    if (itemKey === 'updatePassword') {
      const { updatePassword } = this.props;
      updatePassword();
      return;
    }
    if (itemKey === 'logout') {
      const { logout } = this.props;
      logout();
      return;
    }

    this.handleLanguageSelect(itemKey)();
  };

  onToggleMobileDrawer = () => {
    const { userInfo, pathname } = this.props;
    const { mobileDrawerOpening } = this.state;
    const companyID = getCompanyID(userInfo);
    if (!mobileDrawerOpening) {
      const eventName =
        pathname === '/home' ? event.HOMEPAGE_CLICKS : event.BOOKINGS_CLICKS;
      const categoryName =
        pathname === '/home' ? cat.GE_HOMEPAGE : cat.GE_BOOKINGS;
      trackClick(eventName, categoryName, 'MOBILE_MENU', {
        [custom.COMPANY_ID]: companyID,
      });
    }
    this.setState({
      mobileDrawerOpening: !mobileDrawerOpening,
    });

    if (SCRIBE_COMMON_EVENTS_STATE_PATH_MAPPING.MOBILE_MENU[pathname]) {
      scribeTrackEvent({
        stateName:
          SCRIBE_COMMON_EVENTS_STATE_PATH_MAPPING.MOBILE_MENU[pathname],
        eventName: SCRIBE_COMMON_EVENTS.MOBILE_MENU,
        facets: getCommonFacet(userInfo),
      });
    }
  };

  handleLanguageSelect = lang => async () => {
    if (SUPPORTED_LOCALES.includes(lang)) {
      const { userInfo } = this.props;
      await scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_NAVIGATION,
        eventName: SCRIBE_COMMON_EVENTS.LANGUAGE,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.LANGUAGE]: lang,
        },
        forceUpdate: true,
      });
      window.localStorage.setItem(STORAGE_LOCALE, lang);
      window.location.reload();
    }
  };

  profileMenuVisibilityChanged = isVisible => {
    if (!isVisible) return;
    const { userInfo, pathname } = this.props;
    const companyID = getCompanyID(userInfo);
    const eventName =
      pathname === '/home' ? event.HOMEPAGE_CLICKS : event.BOOKINGS_CLICKS;
    const categoryName =
      pathname === '/home' ? cat.GE_HOMEPAGE : cat.GE_BOOKINGS;
    trackClick(eventName, categoryName, 'PROFILE', {
      [custom.COMPANY_ID]: companyID,
    });

    if (SCRIBE_COMMON_EVENTS_STATE_PATH_MAPPING.PROFILE[pathname]) {
      scribeTrackEvent({
        stateName: SCRIBE_COMMON_EVENTS_STATE_PATH_MAPPING.PROFILE[pathname],
        eventName: SCRIBE_COMMON_EVENTS.PROFILE,
        facets: getCommonFacet(userInfo),
      });
    }
  };

  renderMenu = menuMeta => {
    const { userInfo } = this.props;
    const { currentProfileName, currentGroupName, profiles } = menuMeta;
    const userName = getUserName(userInfo);

    if (!userName) return null;

    return (
      <Dropdown
        overlay={
          <Menu className={css.menu} onClick={this.onClickMenuItem}>
            <Item className={css.userInfo}>
              <h5>{userName}</h5>
            </Item>
            <CompanySelectorDesktop
              profiles={profiles}
              currentProfileName={currentProfileName}
              currentGroupName={currentGroupName}
            />
            <LanguageSelector userInfo={userInfo} />
            {!isPaxUser() && (
              <Item key="updatePassword" className={css.updatePasswordMobile}>
                <Icon type="edit" /> {I18n.t('v2.homepage.updatePassword')}
              </Item>
            )}
            <Item key="logout" className={css.menuItem}>
              <Icon type="logout" /> {I18n.t('v2.homepage.logout')}
            </Item>
          </Menu>
        }
        overlayClassName={css.menuOverlay}
        onVisibleChange={this.profileMenuVisibilityChanged}
      >
        <div className={css.avatar}>
          <Avatar size={'small'}>{userName.charAt(0).toUpperCase()}</Avatar>
        </div>
      </Dropdown>
    );
  };

  renderMenuMobile = menuMeta => {
    const { currentProfileName, currentGroupName, profiles } = menuMeta;
    const { mobileDrawerOpening } = this.state;
    const { logout, userInfo } = this.props;

    return (
      <Fragment>
        <DivButton onClick={this.onToggleMobileDrawer}>
          <img src={require('public/icons/menu.svg')} alt="" />
        </DivButton>
        <Drawer
          width={234}
          placement="right"
          closable={false}
          onClose={this.onToggleMobileDrawer}
          visible={mobileDrawerOpening}
          className={css.menuMobile}
        >
          <Icon
            type="close"
            onClick={this.onToggleMobileDrawer}
            className={css.closeDrawerIcon}
          />
          <AppSider showMobileView />
          <CompanySelectorMobile
            profiles={profiles}
            currentProfileName={currentProfileName}
            currentGroupName={currentGroupName}
            onSelectProfile={this.onChangeProfileAndGroup}
          />
          <div className={css.divider} />
          <LanguageSelector
            handleLanguageSelect={this.handleLanguageSelect}
            userInfo={userInfo}
          />
          {!isPaxUser() && (
            <DivButton onClick={logout} className={css.updatePasswordMobile}>
              {I18n.t('v2.homepage.updatePassword')}
            </DivButton>
          )}
          <DivButton onClick={logout} className={css.logoutMobile}>
            {I18n.t('v2.homepage.logout')}
          </DivButton>
        </Drawer>
      </Fragment>
    );
  };

  render() {
    const { userInfo } = this.props;
    const companies = _get(userInfo, 'companies', []);

    let menuMeta = {
      profiles: isPaxUser() ? [PAX_USER_PROFILE, ...companies] : companies,
    };

    if (isUsingPaxPersonalPayments()) {
      menuMeta = {
        ...menuMeta,
        currentProfileName: I18n.t('appBar.personal'),
        currentGroupName: '', // no need to display
      };
    } else {
      menuMeta = {
        ...menuMeta,
        currentProfileName: getCompanyName(userInfo) || '',
        currentGroupName: getGroupName(userInfo) || '',
      };
    }

    return (
      <div>
        <ExceptMobile>{this.renderMenu(menuMeta)}</ExceptMobile>
        <Mobile>{this.renderMenuMobile(menuMeta)}</Mobile>
      </div>
    );
  }
}

AppMenu.propTypes = {
  userInfo: PropTypes.shape({}).isRequired,
  logout: PropTypes.func.isRequired,
  updatePassword: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
};

export default AppMenu;
